import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../../layout/DashboardLayout";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Paper,
  Chip,
  TablePagination,
  Alert,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useAuth } from "../../../context/AuthContext";
import { getAgencies, getAnalytics, getAdminUsers } from "../../../api/auth";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import BusinessIcon from "@mui/icons-material/Business";
import VerifiedIcon from "@mui/icons-material/Verified";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import EngineeringIcon from "@mui/icons-material/Engineering";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import { brand } from "../../../theme";
import { ROLES } from "../../../constants/roles";
import { EmptyState } from "../../../components/EmptyState";

const cardMeta = {
  total: { color: brand.terracotta, bg: brand.terracottaTint, icon: <BusinessIcon /> },
  tier1: { color: brand.black, bg: "#ede9fe", icon: <VerifiedIcon /> },
  tier2: { color: "#d97706", bg: "#fef3c7", icon: <WorkspacePremiumIcon /> },
  tier3: { color: "#16a34a", bg: "#dcfce7", icon: <EmojiEventsIcon /> },
  [ROLES.SALES_ACCOUNT_SPECIALIST]: { color: brand.goldDark, bg: "#FBF3D9", icon: <SupportAgentIcon /> },
  [ROLES.HELPDESK]: { color: "#e11d48", bg: "#ffe4e6", icon: <HeadphonesIcon /> },
  [ROLES.TECHNICAL_SUPPORT_SPECIALIST]: { color: "#64748b", bg: "#f1f5f9", icon: <EngineeringIcon /> },
};

const tierChipProps = {
  "0": { label: "Tier 0", sx: { bgcolor: "#f1f5f9", color: "#64748b", fontWeight: 600, fontSize: "0.7rem" } },
  "1": { label: "Tier 1", sx: { bgcolor: "#ede9fe", color: "#6d28d9", fontWeight: 600, fontSize: "0.7rem" } },
  "2": { label: "Tier 2", sx: { bgcolor: "#fef3c7", color: "#b45309", fontWeight: 600, fontSize: "0.7rem" } },
  "3": { label: "Tier 3", sx: { bgcolor: "#dcfce7", color: "#15803d", fontWeight: 600, fontSize: "0.7rem" } },
};

const stageChipSx = (status) => {
  switch (status?.toLowerCase()) {
    case "submitted":
    case "approved":
    case "signed":
      return { bgcolor: "#dcfce7", color: "#15803d" };
    case "pending":
      return { bgcolor: "#fef3c7", color: "#b45309" };
    case "rejected":
    case "not signed":
    case "not submitted":
      return { bgcolor: "#fee2e2", color: "#b91c1c" };
    default:
      return { bgcolor: "#f1f5f9", color: "#64748b" };
  }
};

export default function SuperAdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCard, setSelectedCard] = useState("total");
  const [agencySearch, setAgencySearch] = useState("");
  const [agencyPage, setAgencyPage] = useState(0);
  const [agencyRPP, setAgencyRPP] = useState(10);
  const [adminsPage, setAdminsPage] = useState(0);
  const [adminsRPP, setAdminsRPP] = useState(10);

  useEffect(() => {
    (async () => {
      try {
        const [agencyRes, adminRes, analyticsRes] = await Promise.all([
          getAgencies(),
          getAdminUsers(
            `${ROLES.SALES_ACCOUNT_SPECIALIST},${ROLES.HELPDESK},${ROLES.TECHNICAL_SUPPORT_SPECIALIST}`
          ),
          getAnalytics(),
        ]);
        setAgencies(agencyRes.data || []);
        setAdmins(adminRes.data || []);
        setAnalyticsData(analyticsRes.data || []);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load dashboard data. Please refresh the page and try again.");
      } finally {
        setFetching(false);
      }
    })();
  }, []);

  if (loading || fetching) {
    return (
      <DashboardLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      </DashboardLayout>
    );
  }

  const agenciesWithTier = agencies.map((agency) => {
    let tier = "0";
    if (agency.kycStatus === "Submitted") tier = "1";
    if (agency.pccStatus === "Approved") tier = "2";
    if (agency.contractStatus === "Signed") tier = "3";
    return { ...agency, tier };
  });

  const totalAgencies = agenciesWithTier.length;
  const tier1 = agenciesWithTier.filter((a) => a.tier === "1").length;
  const tier2 = agenciesWithTier.filter((a) => a.tier === "2").length;
  const tier3 = agenciesWithTier.filter((a) => a.tier === "3").length;

  const roleCounts = admins.reduce(
    (acc, admin) => { acc[admin.role] = (acc[admin.role] || 0) + 1; return acc; },
    { [ROLES.SALES_ACCOUNT_SPECIALIST]: 0, [ROLES.HELPDESK]: 0, [ROLES.TECHNICAL_SUPPORT_SPECIALIST]: 0 }
  );

  const cardData = [
    { label: "Total Agencies", value: totalAgencies, key: "total" },
    { label: "Tier 1", value: tier1, key: "tier1" },
    { label: "Tier 2", value: tier2, key: "tier2" },
    { label: "Tier 3", value: tier3, key: "tier3" },
    { label: "Sales/Account", value: roleCounts[ROLES.SALES_ACCOUNT_SPECIALIST], key: ROLES.SALES_ACCOUNT_SPECIALIST },
    { label: "Helpdesk", value: roleCounts[ROLES.HELPDESK], key: ROLES.HELPDESK },
    { label: "Technical Support", value: roleCounts[ROLES.TECHNICAL_SUPPORT_SPECIALIST], key: ROLES.TECHNICAL_SUPPORT_SPECIALIST },
  ];

  const filteredByCard =
    selectedCard === "total" || !["tier1", "tier2", "tier3"].includes(selectedCard)
      ? agenciesWithTier
      : agenciesWithTier.filter((a) => `tier${a.tier}` === selectedCard);

  const filteredAgencies = filteredByCard.filter((a) => {
    if (!agencySearch) return true;
    const q = agencySearch.toLowerCase();
    return (
      a.name?.toLowerCase().includes(q) ||
      a.location?.toLowerCase().includes(q) ||
      a.assignedSpecialist?.name?.toLowerCase().includes(q) ||
      a.ownerEmail?.toLowerCase().includes(q)
    );
  });

  return (
    <DashboardLayout>
      {/* Page header */}
      <Box mb={4}>
        <Typography variant="h5" sx={{ color: "#0f172a" }}>
          Welcome back, {user?.name || "Admin"}
        </Typography>
        <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
          Here's what's happening across your platform today.
        </Typography>
      </Box>

      {/* Stat Cards */}
      <Grid container spacing={2.5} mb={4}>
        {cardData.map((card) => {
          const isSelected = selectedCard === card.key;
          const meta = cardMeta[card.key] || cardMeta.total;
          return (
            <Grid item xs={6} sm={4} md={3} lg={12 / 7} key={card.key}>
              <Card
                onClick={() => card.value > 0 && (setSelectedCard(card.key), setAgencyPage(0))}
                sx={{
                  cursor: card.value > 0 ? "pointer" : "default",
                  opacity: card.value === 0 ? 0.55 : 1,
                  borderColor: isSelected ? meta.color : "#e2e8f0",
                  borderWidth: isSelected ? 2 : 1,
                  boxShadow: isSelected
                    ? `0 0 0 3px ${meta.color}22`
                    : "none",
                  transition: "all 0.15s ease",
                }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={1.5}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: "10px",
                        bgcolor: meta.bg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "& svg": { fontSize: "1.2rem", color: meta.color },
                      }}
                    >
                      {meta.icon}
                    </Box>
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 800, fontSize: "1.75rem", color: "#0f172a", lineHeight: 1 }}
                  >
                    {card.value}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#64748b", mt: 0.5, fontWeight: 500, fontSize: "0.8rem" }}
                  >
                    {card.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Activity Chart */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box mb={2.5}>
          <Typography variant="h6" sx={{ color: "#0f172a" }}>
            Activity — Last 8 Weeks
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            KYC submissions, PCC requests, and new agency registrations
          </Typography>
        </Box>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={analyticsData} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis
              dataKey="week"
              tick={{ fontSize: 12, fill: "#94a3b8" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 12, fill: "#94a3b8" }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{
                borderRadius: 10,
                border: "1px solid #e2e8f0",
                boxShadow: "0 10px 40px rgba(15,23,42,0.12)",
                fontSize: 13,
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: 13, paddingTop: 12 }}
              iconType="circle"
              iconSize={8}
            />
            <Bar dataKey="kyc" name="KYC" fill="#E2553C" radius={[4, 4, 0, 0]} maxBarSize={28} />
            <Bar dataKey="pcc" name="PCC" fill="#d97706" radius={[4, 4, 0, 0]} maxBarSize={28} />
            <Bar dataKey="agencies" name="Agencies" fill="#16a34a" radius={[4, 4, 0, 0]} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {/* Agency Table */}
      <Paper>
        <Box px={3} py={2.5} sx={{ borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ color: "#0f172a" }}>
              Agency List
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              {filteredAgencies.length} agenc{filteredAgencies.length === 1 ? "y" : "ies"}
              {selectedCard !== "total" ? ` — filtered by ${selectedCard}` : ""}
            </Typography>
          </Box>
          <TextField
            placeholder="Search by name, location, or specialist…"
            value={agencySearch}
            onChange={(e) => { setAgencySearch(e.target.value); setAgencyPage(0); }}
            size="small"
            sx={{ width: { xs: "100%", sm: 300 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "1rem", color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {filteredAgencies.length === 0 ? (
          <EmptyState
            icon={<BusinessIcon sx={{ fontSize: "2.5rem", color: "#cbd5e1" }} />}
            dashed={false}
            description={
              agenciesWithTier.length === 0
                ? "No agencies registered yet."
                : agencySearch
                  ? "No agencies match your search."
                  : "No agencies match this filter."
            }
            action={
              agenciesWithTier.length > 0
                ? {
                    label: agencySearch ? "Clear search" : "Clear filter",
                    onClick: () => {
                      setAgencySearch("");
                      setSelectedCard("total");
                      setAgencyPage(0);
                    },
                  }
                : undefined
            }
          />
        ) : (
          <>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Agency</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Sales/Account Specialist</TableCell>
                <TableCell>KYC</TableCell>
                <TableCell>PCC</TableCell>
                <TableCell>Contract</TableCell>
                <TableCell>Tier</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAgencies.slice(agencyPage * agencyRPP, agencyPage * agencyRPP + agencyRPP).map((agency) => {
                const initials = agency.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2);
                const chip = tierChipProps[agency.tier] || tierChipProps["0"];
                return (
                  <TableRow key={agency._id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            fontSize: "0.8rem",
                            bgcolor: brand.terracotta,
                            fontWeight: 700,
                          }}
                        >
                          {initials}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600} color="#0f172a">
                            {agency.name}
                          </Typography>
                          <Typography variant="caption" color="#94a3b8">
                            {agency.ownerEmail}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#475569">
                        {agency.location}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={agency.assignedSpecialist ? "#0f172a" : "#94a3b8"} fontWeight={agency.assignedSpecialist ? 500 : 400}>
                        {agency.assignedSpecialist?.name || "Unassigned"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={agency.kycStatus} size="small" sx={{ fontWeight: 600, fontSize: "0.7rem", ...stageChipSx(agency.kycStatus) }} />
                    </TableCell>
                    <TableCell>
                      <Chip label={agency.pccStatus} size="small" sx={{ fontWeight: 600, fontSize: "0.7rem", ...stageChipSx(agency.pccStatus) }} />
                    </TableCell>
                    <TableCell>
                      <Chip label={agency.contractStatus} size="small" sx={{ fontWeight: 600, fontSize: "0.7rem", ...stageChipSx(agency.contractStatus) }} />
                    </TableCell>
                    <TableCell>
                      <Chip label={chip.label} size="small" sx={chip.sx} />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate(`/agencies/${agency._id}`)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredAgencies.length}
            rowsPerPage={agencyRPP}
            page={agencyPage}
            onPageChange={(_, newPage) => setAgencyPage(newPage)}
            onRowsPerPageChange={(e) => { setAgencyRPP(parseInt(e.target.value, 10)); setAgencyPage(0); }}
          />
          </>
        )}
      </Paper>

      {/* Team Members */}
      <Paper sx={{ mt: 3 }}>
        <Box px={3} py={2.5} sx={{ borderBottom: "1px solid #f1f5f9" }}>
          <Typography variant="h6" sx={{ color: "#0f172a" }}>
            Team Members
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            {admins.length} team member{admins.length !== 1 ? "s" : ""}
          </Typography>
        </Box>
        {admins.length === 0 ? (
          <EmptyState
            variant="compact"
            icon={<PeopleAltIcon sx={{ fontSize: "1.5rem", color: "#cbd5e1" }} />}
            description="No team members yet. Invite one from Manage Admins."
          />
        ) : (
          <>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {admins.slice(adminsPage * adminsRPP, adminsPage * adminsRPP + adminsRPP).map((admin) => {
                const meta = cardMeta[admin.role] || cardMeta.total;
                const initials = (admin.name || admin.email || "?").slice(0, 2).toUpperCase();
                return (
                  <TableRow key={admin._id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Avatar sx={{ width: 34, height: 34, fontSize: "0.75rem", bgcolor: meta.color, fontWeight: 700 }}>
                          {initials}
                        </Avatar>
                        <Typography variant="body2" fontWeight={600} color="#0f172a">
                          {admin.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#64748b">{admin.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={admin.role?.replace(/_/g, " ")}
                        size="small"
                        sx={{ bgcolor: meta.bg, color: meta.color, fontWeight: 600, fontSize: "0.7rem" }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={admins.length}
            rowsPerPage={adminsRPP}
            page={adminsPage}
            onPageChange={(_, newPage) => setAdminsPage(newPage)}
            onRowsPerPageChange={(e) => { setAdminsRPP(parseInt(e.target.value, 10)); setAdminsPage(0); }}
          />
          </>
        )}
      </Paper>
    </DashboardLayout>
  );
}
